import { tgpu, d, std } from 'typegpu';
import type { TgpuRoot, TgpuBuffer, StorageFlag, UniformFlag } from 'typegpu';
import type { WgslArray } from 'typegpu/data';
import { RigidState, RigidParams, DataCell } from './types';
import { computeAccelerations } from './deriv';
import { samplePerturb } from '../shared/hash';
import { NbParams } from '../shared/neighbor';
import type { NeighborPipelinesResult } from '../shared/SingleStateSystem';

const TWO_PI = 6.28318530718;
const IntRigid = d.struct({ t1: d.f32, w1: d.f32, t2: d.f32, w2: d.f32 });

export interface RigidNeighborOpts {
  root: TgpuRoot;
  StateArray: WgslArray<typeof RigidState>;
  DataArray: WgslArray<typeof DataCell>;
  NbStackArray: WgslArray<typeof RigidState>;
  paramsBuffer: TgpuBuffer<typeof RigidParams> & UniformFlag;
  nbParamsBuffer: TgpuBuffer<typeof NbParams> & UniformFlag;
  baseA: TgpuBuffer<WgslArray<typeof RigidState>> & StorageFlag;
  baseB: TgpuBuffer<WgslArray<typeof RigidState>> & StorageFlag;
  nbPertA: TgpuBuffer<WgslArray<typeof RigidState>> & StorageFlag;
  nbPertB: TgpuBuffer<WgslArray<typeof RigidState>> & StorageFlag;
  nbDataBuf: TgpuBuffer<WgslArray<typeof DataCell>> & StorageFlag;
}

export function buildRigidNeighborPipelines(o: RigidNeighborOpts): NeighborPipelinesResult {
  const root = o.root;

  const initLayout = tgpu.bindGroupLayout({
    base: { storage: o.StateArray, access: 'mutable' },
    pert: { storage: o.NbStackArray, access: 'mutable' },
    data: { storage: o.DataArray, access: 'mutable' },
    params: { uniform: RigidParams },
    nb: { uniform: NbParams },
  });
  const stepLayout = tgpu.bindGroupLayout({
    baseCur: { storage: o.StateArray },
    baseNext: { storage: o.StateArray, access: 'mutable' },
    pertCur: { storage: o.NbStackArray },
    pertNext: { storage: o.NbStackArray, access: 'mutable' },
    data: { storage: o.DataArray, access: 'mutable' },
    params: { uniform: RigidParams },
    nb: { uniform: NbParams },
  });

  const il = initLayout;
  const sl = stepLayout;

  const circDiff = (a: number) => { 'use gpu'; return a - std.floor(a / TWO_PI + 0.5) * TWO_PI; };

  const integrate = (t1: number, w1: number, t2: number, w2: number, m1: number, m2: number, L1: number, L2: number, dt: number) => {
    'use gpu';
    const a0 = computeAccelerations(t1, w1, t2, w2, m1, m2, L1, L2);
    const hw1 = w1 + 0.5 * dt * a0.x; const hw2 = w2 + 0.5 * dt * a0.y;
    const nt1 = t1 + dt * hw1; const nt2 = t2 + dt * hw2;
    const a1 = computeAccelerations(nt1, hw1, nt2, hw2, m1, m2, L1, L2);
    return IntRigid({ t1: nt1, w1: hw1 + 0.5 * dt * a1.x, t2: nt2, w2: hw2 + 0.5 * dt * a1.y });
  };

  const nbInitCell = (ci: number) => {
    'use gpu';
    const p = il.$.params; const np = il.$.nb;
    const res = d.u32(p.resolution);
    const x = ci % res; const y = ci / res;
    const u = d.f32(x) / p.resolution; const v = d.f32(y) / p.resolution;
    const t1 = (1 - u) * (1 - v) * p.c00_t1 + u * (1 - v) * p.c10_t1 + (1 - u) * v * p.c01_t1 + u * v * p.c11_t1;
    const w1 = (1 - u) * (1 - v) * p.c00_w1 + u * (1 - v) * p.c10_w1 + (1 - u) * v * p.c01_w1 + u * v * p.c11_w1;
    const t2 = (1 - u) * (1 - v) * p.c00_t2 + u * (1 - v) * p.c10_t2 + (1 - u) * v * p.c01_t2 + u * v * p.c11_t2;
    const w2 = (1 - u) * (1 - v) * p.c00_w2 + u * (1 - v) * p.c10_w2 + (1 - u) * v * p.c01_w2 + u * v * p.c11_w2;
    il.$.base[ci] = RigidState({ theta1: t1, omega1: w1, theta2: t2, omega2: w2 });
    const K = d.u32(np.K);
    const cc = res * res;
    const px = d.f32(x) / p.resolution; const py = d.f32(y) / p.resolution;
    for (let k = 0; k < K; k++) {
      const kf = d.f32(k);
      const h1 = samplePerturb(px * 1000 + np.seed + kf * 131.5, py * 1000 + np.seed, np.perturb, np.perturbMode);
      const h2 = samplePerturb(px * 1000 + 100 + np.seed + kf * 131.5, py * 1000 + np.seed, np.perturb, np.perturbMode);
      const idx = d.u32(k) * cc + ci;
      il.$.pert[idx] = RigidState({ theta1: t1 + h1, omega1: w1, theta2: t2 + h2, omega2: w2 });
    }
    il.$.data[ci] = DataCell({ r: 0, g: 0, b: 0, a: 0 });
  };

  const nbStepCell = (ci: number) => {
    'use gpu';
    const p = sl.$.params; const np = sl.$.nb; const dt = p.dt;
    const res = d.u32(p.resolution); const cc = res * res;
    const base = sl.$.baseCur[ci];
    const bn = integrate(base.theta1, base.omega1, base.theta2, base.omega2, p.m1, p.m2, p.L1, p.L2, dt);
    sl.$.baseNext[ci] = RigidState({ theta1: bn.t1, omega1: bn.w1, theta2: bn.t2, omega2: bn.w2 });
    const mode = np.nbMode;
    const K = d.u32(np.K);
    const px = d.f32(ci % res) / p.resolution; const py = d.f32(ci / res) / p.resolution;
    if (mode < 1.5) {
      let best = 0.0;
      for (let k = 0; k < K; k++) {
        const idx = d.u32(k) * cc + ci;
        const pert = sl.$.pertCur[idx];
        const pn = integrate(pert.theta1, pert.omega1, pert.theta2, pert.omega2, p.m1, p.m2, p.L1, p.L2, dt);
        sl.$.pertNext[idx] = RigidState({ theta1: pn.t1, omega1: pn.w1, theta2: pn.t2, omega2: pn.w2 });
        const dd1 = circDiff(pn.t1 - bn.t1); const dd2 = pn.w1 - bn.w1;
        const dd3 = circDiff(pn.t2 - bn.t2); const dd4 = pn.w2 - bn.w2;
        const dist = std.sqrt(dd1 * dd1 + dd2 * dd2 + dd3 * dd3 + dd4 * dd4);
        if (dist > best) best = dist;
      }
      sl.$.data[ci] = DataCell({ r: 0, g: 0, b: best, a: 1 });
    } else if (mode < 2.5) {
      let acc = 0.0;
      for (let k = 0; k < K; k++) {
        const idx = d.u32(k) * cc + ci;
        const pert = sl.$.pertCur[idx];
        const pn = integrate(pert.theta1, pert.omega1, pert.theta2, pert.omega2, p.m1, p.m2, p.L1, p.L2, dt);
        sl.$.pertNext[idx] = RigidState({ theta1: pn.t1, omega1: pn.w1, theta2: pn.t2, omega2: pn.w2 });
        const dd1 = circDiff(pn.t1 - bn.t1); const dd2 = pn.w1 - bn.w1;
        const dd3 = circDiff(pn.t2 - bn.t2); const dd4 = pn.w2 - bn.w2;
        acc = acc + std.sqrt(dd1 * dd1 + dd2 * dd2 + dd3 * dd3 + dd4 * dd4);
      }
      sl.$.data[ci] = DataCell({ r: 0, g: 0, b: acc, a: 1 });
    } else {
      const sigma = np.perturb;
      let sumLog = 0.0;
      for (let k = 0; k < K; k++) {
        const kf = d.f32(k);
        const h1 = samplePerturb(px * 1000 + np.seed + kf * 131.5, py * 1000 + np.seed, sigma, np.perturbMode);
        const h2 = samplePerturb(px * 1000 + 100 + np.seed + kf * 131.5, py * 1000 + np.seed, sigma, np.perturbMode);
        const pn = integrate(base.theta1 + h1, base.omega1, base.theta2 + h2, base.omega2, p.m1, p.m2, p.L1, p.L2, dt);
        const dd1 = circDiff(pn.t1 - bn.t1); const dd2 = pn.w1 - bn.w1;
        const dd3 = circDiff(pn.t2 - bn.t2); const dd4 = pn.w2 - bn.w2;
        const dist = std.sqrt(dd1 * dd1 + dd2 * dd2 + dd3 * dd3 + dd4 * dd4);
        sumLog = sumLog + std.log(std.max(dist, 1e-12) / sigma);
      }
      const gmLog = sumLog / np.K;
      const data = sl.$.data[ci];
      let count = data.a; let sumv = data.b;
      if (count < 0.5) { count = 1; sumv = gmLog; } else { count = count + 1; sumv = sumv + gmLog; }
      sl.$.data[ci] = DataCell({ r: 0, g: 0, b: (sumv / count) / dt, a: count });
    }
  };

  return {
    nbInitPipeline: root.createGuardedComputePipeline(nbInitCell),
    nbStepPipeline: root.createGuardedComputePipeline(nbStepCell),
    nbInitBG: root.createBindGroup(initLayout, {
      base: o.baseA, pert: o.nbPertA, data: o.nbDataBuf,
      params: o.paramsBuffer, nb: o.nbParamsBuffer,
    }),
    nbStepFwd: root.createBindGroup(stepLayout, {
      baseCur: o.baseA, baseNext: o.baseB, pertCur: o.nbPertA, pertNext: o.nbPertB,
      data: o.nbDataBuf, params: o.paramsBuffer, nb: o.nbParamsBuffer,
    }),
    nbStepBwd: root.createBindGroup(stepLayout, {
      baseCur: o.baseB, baseNext: o.baseA, pertCur: o.nbPertB, pertNext: o.nbPertA,
      data: o.nbDataBuf, params: o.paramsBuffer, nb: o.nbParamsBuffer,
    }),
  };
}